import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import {
  REVIEW_REASON_OPTIONS, REVIEW_CONFIDENCE_OPTIONS,
} from "./ReviewIntelligencePrompt";

/**
 * @param {{ open: boolean, record: any, onClose: () => void, onSave: (payload: any) => Promise<void> }} props
 */
export default function EditRecordModal({ open, record, onClose, onSave }) {
  const [description, setDescription] = useState("");
  const [counterparty, setCounterparty] = useState("");
  const [reference, setReference] = useState("");
  const [sourceAmount, setSourceAmount] = useState("");
  const [sourceCurrency, setSourceCurrency] = useState("GBP");
  const [reportingAmount, setReportingAmount] = useState("");
  const [reportingCurrency, setReportingCurrency] = useState("GBP");
  const [reason, setReason] = useState("");
  // P3 — Review Intelligence (required)
  const [reasonCode, setReasonCode] = useState("");
  const [userConfidence, setUserConfidence] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) {
      const np = record.normalized_payload || {};
      setDescription(np.description || "");
      setCounterparty(np.counterparty || "");
      setReference(np.reference || "");
      setSourceAmount(String(np.source_amount || 0));
      setSourceCurrency(np.source_currency || "GBP");
      setReportingAmount(String(np.reporting_amount || 0));
      setReportingCurrency(np.reporting_currency || "GBP");
      setReason("");
      setReasonCode("");
      setUserConfidence("");
      setReasonText("");
    }
  }, [record]);

  const valid = Boolean(reason.trim()) && Boolean(reasonCode) && Boolean(userConfidence);

  const save = async () => {
    if (!valid || !record) return;
    setSaving(true);
    try {
      await onSave({
        description,
        counterparty,
        reference,
        source_amount: parseFloat(sourceAmount) || 0,
        source_currency: sourceCurrency,
        reporting_amount: parseFloat(reportingAmount) || 0,
        reporting_currency: reportingCurrency,
        override_reason: reason,
        reason_code: reasonCode,
        user_confidence: userConfidence,
        reason_text: reasonText.trim() || undefined,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;
  const np = record.normalized_payload || {};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1120] border-slate-800 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-record-modal">
        <DialogHeader>
          <DialogTitle className="font-barlow text-slate-50">Edit Record</DialogTitle>
          <DialogDescription className="text-slate-500">
            Editing returns the record to needs_review. All original values are preserved in history.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Original</div>
            <div className="text-xs text-slate-400">{np.description || "No description"}</div>
            <div className="text-[10px] text-slate-600 font-mono">
              {np.source_currency} {np.source_amount}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)}
              className="bg-slate-900/50 border-slate-700 mt-1 text-xs" data-testid="edit-field-description" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Counterparty</Label>
            <Input value={counterparty} onChange={(e) => setCounterparty(e.target.value)}
              className="bg-slate-900/50 border-slate-700 mt-1 text-xs" data-testid="edit-field-counterparty" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Reference</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)}
              className="bg-slate-900/50 border-slate-700 mt-1 text-xs" data-testid="edit-field-reference" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-400">Source amount</Label>
              <Input type="number" value={sourceAmount} onChange={(e) => setSourceAmount(e.target.value)}
                className="bg-slate-900/50 border-slate-700 mt-1 text-xs font-mono" data-testid="edit-field-source-amount" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Source currency</Label>
              <Input value={sourceCurrency} onChange={(e) => setSourceCurrency(e.target.value.toUpperCase())}
                className="bg-slate-900/50 border-slate-700 mt-1 text-xs font-mono" data-testid="edit-field-source-currency" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Reporting amount</Label>
              <Input type="number" value={reportingAmount} onChange={(e) => setReportingAmount(e.target.value)}
                className="bg-slate-900/50 border-slate-700 mt-1 text-xs font-mono" data-testid="edit-field-reporting-amount" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Reporting currency</Label>
              <Input value={reportingCurrency} onChange={(e) => setReportingCurrency(e.target.value.toUpperCase())}
                className="bg-slate-900/50 border-slate-700 mt-1 text-xs font-mono" data-testid="edit-field-reporting-currency" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Reason for edit (required)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this correction needed?"
              className="bg-slate-900/50 border-slate-700 mt-1 min-h-[60px] text-xs" data-testid="edit-field-reason" />
          </div>

          {/* P3 Review Intelligence — required */}
          <div data-testid="edit-review-reason-field">
            <Label className="text-xs text-slate-400">
              Why are you changing this?
              <span className="ml-2 text-[10px] text-amber-400">required</span>
            </Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger
                className="bg-slate-900/50 border-slate-700 text-xs mt-1"
                data-testid="edit-review-reason-trigger"
              >
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {REVIEW_REASON_OPTIONS.map((r) => (
                  <SelectItem
                    key={r.value} value={r.value}
                    data-testid={`edit-review-reason-option-${r.value}`}
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div data-testid="edit-review-confidence-field">
            <Label className="text-xs text-slate-400">
              How confident are you?
              <span className="ml-2 text-[10px] text-amber-400">required</span>
            </Label>
            <div
              className="grid grid-cols-3 gap-2 mt-1.5"
              role="radiogroup"
              aria-label="Reviewer confidence"
            >
              {REVIEW_CONFIDENCE_OPTIONS.map((c) => {
                const active = userConfidence === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setUserConfidence(c.value)}
                    aria-checked={active}
                    role="radio"
                    data-testid={`edit-review-confidence-${c.value}`}
                    className={[
                      "text-[11px] font-semibold py-2 rounded-md border transition-colors",
                      active
                        ? "border-cyan-400 bg-cyan-500/15 text-cyan-300"
                        : "border-slate-700 bg-slate-900/50 text-slate-400 hover:border-slate-600 hover:text-slate-200",
                    ].join(" ")}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div data-testid="edit-review-note-field">
            <Label className="text-xs text-slate-400">
              Optional note
              <span className="ml-2 text-[10px] text-slate-500">
                helps Sentient Vault learn faster
              </span>
            </Label>
            <Textarea
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value.slice(0, 1000))}
              placeholder="Short context for future learning…"
              className="bg-slate-900/50 border-slate-700 mt-1 min-h-[50px] text-xs"
              data-testid="edit-review-note-input"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-700" data-testid="edit-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving || !valid} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="edit-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Saving...</> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
